import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const METRICS = [
  {
    id: 1,
    icon: 'Users',
    count: '10,000+',
    label: 'Verified players',
    description: 'Every account KYC-checked before the first bet',
    color: '#5B8CFF',
  },
  {
    id: 2,
    icon: 'Zap',
    count: '4m 51s',
    label: 'Average payout',
    description: 'Measured across the last 10,000 withdrawals',
    color: '#25D366',
  },
  {
    id: 3,
    icon: 'ShieldCheck',
    count: '256-bit',
    label: 'Encrypted platform',
    description: 'Bank-grade TLS on every session and transaction',
    color: '#B98CFF',
  },
  {
    id: 4,
    icon: 'MessageCircle',
    count: '24 × 7',
    label: 'Human WhatsApp desk',
    description: 'Real people, no bots, average reply under 2 minutes',
    color: '#F0C24B',
  },
];

const COMPLIANCE = [
  { id: 1, name: 'SSL secured', icon: 'Lock', description: 'End-to-end encryption' },
  { id: 2, name: 'Fair play certified', icon: 'BadgeCheck', description: 'RNG audited monthly' },
  { id: 3, name: 'Licensed operations', icon: 'Award', description: 'Regulated gaming partner' },
  { id: 4, name: 'Responsible gaming', icon: 'HeartHandshake', description: 'Limits, cool-offs, 18+ only' },
];

const PAYMENTS = [
  { id: 1, name: 'UPI', logo: '/assets/images/gen/pay-upi.svg', description: 'Instant deposits, zero fee' },
  { id: 2, name: 'Wallets', logo: '/assets/images/gen/pay-wallet.svg', description: 'Paytm · PhonePe · GPay' },
  { id: 3, name: 'Net banking', logo: '/assets/images/gen/pay-bank.svg', description: 'All Indian banks supported' },
  { id: 4, name: 'USDT', logo: '/assets/images/gen/pay-crypto.svg', description: 'For international players' },
];

const TrustIndicators = () => (
  <section className="relative overflow-hidden py-24">
    <span className="aurora right-[-10%] top-[4%] h-[26rem] w-[26rem] bg-trust-blue/25" aria-hidden="true" />

    <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl text-center">
        <span className="badge-pill"><Icon name="ShieldCheck" size={13} /> Trust &amp; security</span>
        <h2 className="mt-6 font-heading text-display font-bold text-white">
          Why 10,000+ players stay with <span className="gold-text">Nitin</span>
        </h2>
        <p className="mt-4 text-body-lg text-white/60">
          Trust is not a slogan here — it is a set of numbers we publish. Fast withdrawals, audited games and a
          support desk staffed by humans who know your name.
        </p>
      </div>

      {/* Metrics */}
      <div className="perspective mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {METRICS.map((m, i) => (
          <div key={m.id} className="card-3d animate-rise p-7 text-center" style={{ animationDelay: `${i * 80}ms` }}>
            <span
              className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl border border-white/10 shadow-[0_1px_0_rgba(255,255,255,0.2)_inset]"
              style={{ background: `linear-gradient(160deg, ${m.color}33, ${m.color}0D)` }}
            >
              <Icon name={m.icon} size={28} color={m.color} />
            </span>
            <div className="font-display text-3xl font-bold text-white">{m.count}</div>
            <div className="mt-1 font-display text-sm font-semibold text-royal-gold">{m.label}</div>
            <p className="mt-2 text-sm leading-relaxed text-white/50">{m.description}</p>
          </div>
        ))}
      </div>

      {/* Compliance */}
      <div className="card-flat mt-14 p-8 sm:p-10">
        <div className="text-center">
          <h3 className="font-heading text-2xl font-bold text-white">Security &amp; compliance</h3>
          <p className="mt-2 text-white/55">Independent checks, published every month</p>
        </div>

        <div className="mt-9 grid grid-cols-2 gap-6 lg:grid-cols-4">
          {COMPLIANCE.map((b) => (
            <div key={b.id} className="group text-center">
              <span className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-royal-gold/25 bg-royal-gold/10 transition-all duration-500 group-hover:-translate-y-1.5 group-hover:border-royal-gold/70 group-hover:shadow-glow">
                <Icon name={b.icon} size={24} color="#F0C24B" />
              </span>
              <div className="font-display text-sm font-semibold text-white">{b.name}</div>
              <div className="mt-1 text-xs text-white/45">{b.description}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Payments */}
      <div className="mt-14">
        <div className="text-center">
          <h3 className="font-heading text-2xl font-bold text-white">Money in, money out — fast</h3>
          <p className="mt-2 text-white/55">
            Deposit in seconds, withdraw in about five minutes, any hour of the day
          </p>
        </div>

        <div className="perspective mt-9 grid grid-cols-2 gap-5 lg:grid-cols-4">
          {PAYMENTS.map((p) => (
            <div key={p.id} className="card-3d overflow-hidden">
              <Image src={p.logo} alt={p.name} className="h-32 w-full object-cover" />
              <div className="px-5 pb-5 pt-4">
                <div className="font-display text-sm font-semibold text-white">{p.name}</div>
                <div className="mt-1 text-xs text-white/45">{p.description}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pledge */}
      <div className="panel-gold shine-hover mt-14 overflow-hidden px-6 py-10 text-center sm:px-12">
        <span className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-royal-gold/15">
          <Icon name="Award" size={28} color="#F0C24B" />
        </span>
        <h3 className="font-heading text-2xl font-bold text-white sm:text-3xl">The Nitin guarantee</h3>
        <p className="mx-auto mt-4 max-w-2xl text-body-lg italic text-white/70">
          “Three years running, zero security incidents, and not a single withdrawal request left unanswered
          overnight. If a payout is ever late, message the desk and we escalate it in minutes.”
        </p>
        <div className="mt-9 grid grid-cols-3 gap-6">
          {[
            { v: '4.9/5', l: 'Player rating' },
            { v: '₹50Cr+', l: 'Paid to winners' },
            { v: '3 Years', l: 'Unbroken service' },
          ].map((s) => (
            <div key={s.l}>
              <div className="font-display text-2xl font-bold gold-text">{s.v}</div>
              <div className="mt-1 text-xs uppercase tracking-[0.14em] text-white/45">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default TrustIndicators;
